import { Drawer, TextField, Button, Typography, Box, Checkbox, FormControlLabel, MenuItem, Select, CircularProgress } from '@mui/material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Copy, X } from 'lucide-react';
import ImageUploadPreview from '../ui/ImageUpload/ImageUploadPreview';
import { uploadImage } from '../../api/hotelinfoApi';
import { deleteRemovedImages } from '../../utils/imageUtils';

export default function SpaFormModal({ open, onClose, initialData, onSubmit }) {
  const [form, setForm] = useState({});
  const [durationInputIndex, setDurationInputIndex] = useState(null);
  const [newDuration, setNewDuration] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);

  // Ref to track concurrent image uploads
  const uploadCountRef = useRef(0);

  const handleImageUrlsChange = useCallback((updatedUrls) => {
    setForm(prev => ({ ...prev, photos: updatedUrls }));
  }, []);

useEffect(() => {
  setForm({
    ...initialData,
    openingHours: initialData?.openingHours || [],
    massages: initialData?.massages || [],
    photos: initialData?.photos || []
  });
}, [initialData]);
  const handleOpeningHourChange = (index, field, value) => {
    const updated = [...(form.openingHours || [])];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, openingHours: updated }));
  };

  const addOpeningHour = () => {
    setForm(prev => ({
      ...prev,
      openingHours: [...(prev.openingHours || []), { dayOfWeek: '', open: '', close: '' }]
    }));
  };

  const removeOpeningHour = (index) => {
    const updated = [...(form.openingHours || [])];
    updated.splice(index, 1);
    setForm(prev => ({ ...prev, openingHours: updated }));
  };

  const handleMassageChange = (index, field, value) => {
    const updated = [...(form.massages || [])];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, massages: updated }));
  };

  // Remove a duration from a massage entry
  const removeDuration = (massageIndex, duration) => {
    const updated = [...(form.massages || [])];
    const prices = { ...(updated[massageIndex].prices || {}) };
    delete prices[duration];
    updated[massageIndex].prices = prices;
    setForm(prev => ({ ...prev, massages: updated }));
  };

  const handlePriceChange = (index, duration, price) => {
    const updated = [...(form.massages || [])];
    updated[index] = {
      ...updated[index],
      prices: { ...(updated[index].prices || {}), [duration]: price }
    };
    setForm(prev => ({ ...prev, massages: updated }));
  };

  const addMassage = () => {
    setForm(prev => ({
      ...prev,
      massages: [...(prev.massages || []), { typeOfMassage: '', description: '', prices: {} }]
    }));
  };

  const removeMassage = (index) => {
    const updated = [...(form.massages || [])];
    updated.splice(index, 1);
    setForm(prev => ({ ...prev, massages: updated }));
  };

  const duplicateMassage = (index) => {
    const current = form.massages?.[index];
    if (!current) return;
    // Strip identifiers so the backend will generate a fresh subdocument _id
    const { _id, promotion, ...rest } = current || {};
    const cloned = {
      ...rest,
      prices: { ...(current?.prices || {}) },
      promotion: null,
    };
    const updated = [...(form.massages || [])];
    updated.splice(index + 1, 0, cloned);
    setForm(prev => ({ ...prev, massages: updated }));
  };

  const confirmAddDuration = (index) => {
    if (!newDuration) return;
    const updated = [...(form.massages || [])];
    updated[index] = {
      ...updated[index],
      prices: {
        ...(updated[index].prices || {}),
        [newDuration]: 0
      }
    };
    setForm(prev => ({ ...prev, massages: updated }));
    setNewDuration('');
    setDurationInputIndex(null);
  };

  const applyOpeningPreset = (type) => {
    const presets = {
      weekday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      weekend: ['Sat', 'Sun'],
      everyday: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    };

    const entries = presets[type].map(day => ({
      dayOfWeek: day,
      open: '09:00',
      close: '22:00'
    }));

    setForm(prev => ({
      ...prev,
      openingHours: entries
    }));
  };

  const handleSave = () => {
    const filteredPhotos = (form.photos || []).filter(
      (url) => typeof url === 'string' && !url.startsWith('blob:')
    );

    // Ensure no duplicate _id values are sent for massages; if a duplicate occurs,
    // drop the _id so the backend assigns a fresh one.
    const seen = new Set();
    const massagesClean = (form.massages || []).map((m) => {
      const copy = { ...m };
      if (copy._id) {
        if (seen.has(copy._id)) {
          delete copy._id;
        } else {
          seen.add(copy._id);
        }
      }
      return copy;
    });

    const cleanedForm = {
      ...form,
      photos: filteredPhotos,
      massages: massagesClean,
    };

    onSubmit(cleanedForm);

    if (initialData?.photos) {
      setTimeout(() => {
        deleteRemovedImages(initialData.photos, filteredPhotos);
      }, 500);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 600 } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">{form._id ? 'Edit' : 'Add'} Spa Entry</Typography>
      </Box>
      <Box sx={{ p: 3, overflowY: 'auto', height: 'calc(100% - 160px)' }}>
        <TextField
          fullWidth
          label="Spa Name"
          margin="normal"
          value={form.name || ''}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
        />
        <TextField
          fullWidth
          label="Subtitle"
          margin="normal"
          value={form.subtitle || ''}
          onChange={(e) => setForm(prev => ({ ...prev, subtitle: e.target.value }))}
          placeholder="E.g. Traditional Khmer Massage, Natural Healing Spa"
        />
        <TextField
          fullWidth
          label="About"
          multiline
          rows={3}
          margin="normal"
          value={form.about || ''}
          onChange={(e) => setForm(prev => ({ ...prev, about: e.target.value }))}
        />
        <Box mt={2}>
          <Typography fontWeight="bold" gutterBottom>Massage Menu</Typography>
          {(form.massages || []).map((massage, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight="bold">Massage #{index + 1}</Typography>
                <Button size="small" color="error" onClick={() => removeMassage(index)}>
                  <X size={16} />
                </Button>
              </Box>
              <TextField
                fullWidth
                label="Type of Massage"
                margin="dense"
                value={massage.typeOfMassage || ''}
                onChange={(e) => handleMassageChange(index, 'typeOfMassage', e.target.value)}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                margin="dense"
                value={massage.description || ''}
                onChange={(e) => handleMassageChange(index, 'description', e.target.value)}
              />
              <TextField
                fullWidth
                label="Category"
                margin="dense"
                value={massage.category || ''}
                onChange={(e) => handleMassageChange(index, 'category', e.target.value)}
              />
              <Box display="flex" gap={2} mt={1}>
                {Object.keys(massage.prices || { '60': 0, '90': 0 }).map((duration) => (
                  <Box key={duration} display="flex" alignItems="center" gap={1}>
                    <TextField
                      label={`Price (${duration}min)`}
                      type="number"
                      value={massage.prices?.[duration] || 0}
                      onChange={(e) => handlePriceChange(index, duration, Number(e.target.value))}
                    />
                    <Button size="small" color="error" onClick={() => removeDuration(index, duration)}>✕</Button>
                  </Box>
                ))}
              </Box>
              {durationInputIndex === index ? (
                <Box display="flex" gap={1} mt={1}>
                  <TextField
                    label="New Duration (min)"
                    value={newDuration}
                    onChange={(e) => setNewDuration(e.target.value)}
                    type="number"
                    size="small"
                  />
                  <Button size="small" variant="outlined" onClick={() => confirmAddDuration(index)}>Add</Button>
                  <Button size="small" onClick={() => setDurationInputIndex(null)}>Cancel</Button>
                </Box>
              ) : (
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Button onClick={() => setDurationInputIndex(index)} size="small">
                    + Add Duration
                  </Button>
                  <Button size="small" onClick={() => duplicateMassage(index)}>
                    <Copy size={16} />
                  </Button>
                </Box>
              )}
            </Box>
          ))}
          <Button onClick={addMassage} sx={{ mt: 1 }} size="small">+ Add Massage</Button>
        </Box>
        <Box mt={2}>
          <Typography fontWeight="bold" gutterBottom>Opening Hours</Typography>
          <Box display="flex" gap={1} mb={2}>
            <Button variant="outlined" size="small" onClick={() => applyOpeningPreset('weekday')}>Mon–Fri</Button>
            <Button variant="outlined" size="small" onClick={() => applyOpeningPreset('weekend')}>Sat–Sun</Button>
            <Button variant="outlined" size="small" onClick={() => applyOpeningPreset('everyday')}>Everyday</Button>
          </Box>
          {(form.openingHours || []).map((entry, index) => (
            <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
              <Select
                value={entry.dayOfWeek || ''}
                onChange={(e) => handleOpeningHourChange(index, 'dayOfWeek', e.target.value)}
                size="small"
                sx={{ width: 120 }}
                displayEmpty
              >
                <MenuItem value="" disabled>Select Day</MenuItem>
                <MenuItem value="Mon">Mon</MenuItem>
                <MenuItem value="Tue">Tue</MenuItem>
                <MenuItem value="Wed">Wed</MenuItem>
                <MenuItem value="Thu">Thu</MenuItem>
                <MenuItem value="Fri">Fri</MenuItem>
                <MenuItem value="Sat">Sat</MenuItem>
                <MenuItem value="Sun">Sun</MenuItem>
              </Select>
              <TextField
                label="Open"
                type="time"
                value={entry.open || ''}
                onChange={(e) => handleOpeningHourChange(index, 'open', e.target.value)}
                size="small"
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Close"
                type="time"
                value={entry.close || ''}
                onChange={(e) => handleOpeningHourChange(index, 'close', e.target.value)}
                size="small"
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                color="error"
                onClick={() => removeOpeningHour(index)}
              >
                ✕
              </Button>
            </Box>
          ))}
          <Button variant="outlined" onClick={addOpeningHour}>
            Add Opening Hour
          </Button>
        </Box>
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.bookingRequired || false}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, bookingRequired: e.target.checked }))
                }
              />
            }
            label="Booking Required"
          />
        </Box>
        <Box mt={2}>
          <Typography fontWeight="bold" gutterBottom>Photos</Typography>
          <ImageUploadPreview
            initialUrls={form.photos || []}
            onChange={handleImageUrlsChange}
            uploadImageFn={async (file, onProgress) => {
              uploadCountRef.current += 1;
              setUploadingImages(true);
              const url = await uploadImage(file, onProgress);
              uploadCountRef.current -= 1;
              if (uploadCountRef.current === 0) {
                setUploadingImages(false);
              }
              return url;
            }}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ ml: 1 }}
          disabled={uploadingImages}
          startIcon={uploadingImages ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
}